import { getDb } from '@/data/db';
import type { LogEntry } from '@/types/domain';
import { clientId } from '@/lib/utils';

const MAX_ATTEMPTS = 5;

export const logQueueRepo = {
  async enqueue(entry: Omit<LogEntry, 'id' | 'attempts' | 'status'>): Promise<LogEntry> {
    const row: LogEntry = {
      ...entry,
      id: clientId(),
      attempts: 0,
      status: 'pending',
    };
    await getDb().logQueue.add(row);
    return row;
  },

  async pendingBatch(limit = 25): Promise<LogEntry[]> {
    return getDb()
      .logQueue.where('status')
      .anyOf(['pending', 'inflight'])
      .limit(limit)
      .toArray();
  },

  /** Logs still awaiting push (pending/inflight); reported in the heartbeat. */
  async pendingCount(): Promise<number> {
    return getDb().logQueue.where('status').anyOf(['pending', 'inflight']).count();
  },

  async markInflight(ids: string[]): Promise<void> {
    if (!ids.length) return;
    await getDb().logQueue.where('id').anyOf(ids).modify({ status: 'inflight' });
  },

  async markSent(id: string, serverRecordId?: string): Promise<void> {
    await getDb().logQueue.update(id, {
      status: 'sent',
      serverRecordId,
      lastError: undefined,
    });
  },

  async markFailed(id: string, error: string): Promise<void> {
    const row = await getDb().logQueue.get(id);
    if (!row) return;
    const attempts = row.attempts + 1;
    await getDb().logQueue.update(id, {
      attempts,
      lastError: error,
      status: attempts >= MAX_ATTEMPTS ? 'quarantined' : 'pending',
    });
  },

  async purgeSent(olderThanMs = 24 * 60 * 60 * 1000): Promise<number> {
    const cutoff = new Date(Date.now() - olderThanMs).toISOString();
    return getDb().logQueue
      .where('status')
      .equals('sent')
      .and((row) => row.capturedAt < cutoff)
      .delete();
  },

  async getQuarantined(): Promise<LogEntry[]> {
    return getDb().logQueue.where('status').equals('quarantined').toArray();
  },

  /** Pending/inflight logs that have already failed at least once (carry a lastError). */
  async getPendingWithErrors(): Promise<LogEntry[]> {
    return getDb()
      .logQueue.where('status')
      .anyOf(['pending', 'inflight'])
      .filter((r) => !!r.lastError)
      .toArray();
  },

  async getQuarantinedCount(): Promise<number> {
    return getDb().logQueue.where('status').equals('quarantined').count();
  },

  /**
   * Resolve the FileMaker record id for a pushed log (two-step media flow).
   * Returns undefined until that log has been accepted by the server.
   */
  async getServerRecordId(logClientId: string): Promise<string | undefined> {
    const row = await getDb().logQueue.get(logClientId);
    return row?.serverRecordId;
  },

  /**
   * Manual retry hook for quarantined entries (e.g. driver triggers
   * "Retry Sync" from settings).
   */
  async resetQuarantined(): Promise<void> {
    await getDb()
      .logQueue.where('status')
      .equals('quarantined')
      .modify({ status: 'pending', attempts: 0 });
  },
};
